import { NavigationStateInterface, UnsavedChangesModal } from './interfaces';
import {
  NavigationPages,
  NavigationSections,
  NotePageSections,
  SettingsPageSections,
} from '@ax/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export * as NavigationUtils from './utilities';
export * as NavigationSelectors from './selectors';

export const navigationInitialState: NavigationStateInterface = {
  currentPage: NavigationPages.Notes,
  currentSection: NotePageSections.CareCues,
  unsavedChanges: false,
  unsavedChangesSource: null,
  ignoredNavigation: null,
  unsavedChangesModal: {
    open: false,
    title: '',
    message: '',
  },
};

export const navigationSlice = createSlice({
  name: 'navigation',
  initialState: navigationInitialState,
  reducers: {
    pageNavigationRequested(
      state,
      _: PayloadAction<{
        page: NavigationPages;
        section?: NavigationSections;
        params?: Record<string, string>;
      }>,
    ) {
      return {
        ...state,
      };
    },
    pageNavigationSucceeded(
      state,
      action: PayloadAction<{
        page: NavigationPages;
        previous?: NavigationPages;
        section?: NavigationSections;
      }>,
    ) {
      const currentSection =
        action.payload.section ||
        (() => {
          switch (action.payload.page) {
            case NavigationPages.Notes:
              return NotePageSections.CareCues;
            case NavigationPages.Settings:
              return SettingsPageSections.Preferences;
            case NavigationPages.connectedNotes:
              return NotePageSections.Transcript;
          }
        })();

      return {
        ...state,
        currentPage: action.payload.page,
        currentSection,
        ignoredNavigation: null,
      };
    },
    pageNavigationIgnored(
      state,
      action: PayloadAction<{ page: NavigationPages; section?: NavigationSections }>,
    ) {
      return {
        ...state,
        ignoredNavigation: action.payload || null,
        unsavedChangesModal: {
          ...state.unsavedChangesModal,
        },
      };
    },
    sectionNavigationRequested(
      state,
      _: PayloadAction<{ section: NavigationSections; noteId?: string }>,
    ) {
      return {
        ...state,
      };
    },
    sectionNavigationSucceeded(
      state,
      action: PayloadAction<{ section: NavigationSections; noteId?: string }>,
    ) {
      if (!action.payload.section) {
        return { ...state };
      }

      return {
        ...state,
        currentSection: action.payload.section,
      };
    },
    sectionNavigationIgnored(
      state,
      _: PayloadAction<{ section: NavigationSections; noteId?: string }>,
    ) {
      return {
        ...state,
      };
    },
    unsavedChangesDetected(
      state,
      action: PayloadAction<{
        source: NavigationSections;
        noteId: string;
        unsavedChangesModal?: UnsavedChangesModal;
      }>,
    ) {
      if (!action.payload.source || action.payload.source === state.currentSection) {
        return { ...state };
      }

      return {
        ...state,
        unsavedChanges: true,
        unsavedChangesSource: action.payload.source,
        unsavedChangesModal: action.payload.unsavedChangesModal || {
          open: false,
          title: 'Unsaved changes',
          message: 'If you leave this page, your changes will be lost.',
        },
      };
    },
    unsavedChangesResolved(state, _: PayloadAction<{ noteId: string }>) {
      return {
        ...state,
        unsavedChanges: false,
        unsavedChangesSource: null,
      };
    },
    unsavedChangesModalOpened(state, _: PayloadAction<void>) {
      return {
        ...state,
        unsavedChangesModal: {
          ...state.unsavedChangesModal,
          open: true,
        },
      };
    },
    unsavedChangesModalClosed(state, _: PayloadAction<void>) {
      return {
        ...state,
        ignoredNavigation: null,
        unsavedChangesModal: {
          ...state.unsavedChangesModal,
          open: false,
        },
      };
    },
    unsavedChangesModalConfirmed(state, _: PayloadAction<void>) {
      return {
        ...state,
        unsavedChanges: false,
        unsavedChangesModal: {
          open: false,
          title: '',
          message: '',
        },
      };
    },
  },
});
